<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @search="handleSearch"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="settings-view">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('settings.title', 'Settings') }}</h1>
        <p class="page-subtitle">{{ $t('settings.subtitle', 'Manage your business configuration') }}</p>
        <span class="account-id">Account ID: {{ businessId }}</span>
      </div>

      <!-- Tab Navigation -->
      <div class="tab-container">
        <div class="tab-nav">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-button"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            <span class="tab-icon">{{ tab.icon }}</span>
            <span class="tab-label">{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <Transition name="fade" mode="out-in">
            <BusinessSettings v-if="activeTab === 'business'" key="business" />
            <TaxSettings v-else-if="activeTab === 'tax'" key="tax" />
            <LoyaltySettings v-else-if="activeTab === 'loyalty'" key="loyalty" />
            <PrintSettings v-else-if="activeTab === 'print'" key="print" />
            <UnitSettings v-else-if="activeTab === 'units'" key="units" />
            <SignatureSettings v-else-if="activeTab === 'signature'" key="signature" />
          </Transition>
        </div>
      </div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
} from '@shared/components/intercom'
import { billingNavSections } from '../config/navigation.js'

// Settings Components
import BusinessSettings from '../components/settings/BusinessSettings.vue'
import TaxSettings from '../components/settings/TaxSettings.vue'
import LoyaltySettings from '../components/settings/LoyaltySettings.vue'
import PrintSettings from '../components/settings/PrintSettings.vue'
import UnitSettings from '../components/settings/UnitSettings.vue'
import SignatureSettings from '../components/settings/SignatureSettings.vue'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { businessName, fetchBusinessName } = useBilling()
const businessId = computed(() => user.value?.business_id || user.value?.business?.id || null)

const navSections = billingNavSections

onMounted(() => {
  fetchBusinessName()
})

// Tab state
const activeTab = ref('business')

// Tab definitions
const tabs = computed(() => [
  {
    id: 'business',
    label: t('settings.businessInfo', 'Business Info'),
    icon: '🏢',
  },
  {
    id: 'tax',
    label: t('settings.taxSettings', 'Tax Settings'),
    icon: '📋',
  },
  {
    id: 'loyalty',
    label: t('loyalty.title', 'Loyalty Program'),
    icon: '⭐',
  },
  {
    id: 'print',
    label: t('settings.printSettings', 'Print Settings'),
    icon: '🖨',
  },
  {
    id: 'units',
    label: 'Unit Types',
    icon: '[U]',
  },
  {
    id: 'signature',
    label: 'Signature',
    icon: '[S]',
  },
])

const handleSearch = (query) => {
  console.log('Search:', query)
}

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  // Already on settings page
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.settings-view {
  padding: var(--space-6);
  max-width: 1100px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.account-id {
  display: inline-block;
  font-size: 12px;
  color: var(--text-tertiary, #98a2b3);
  background: var(--bg-secondary, #f9fafb);
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  margin-top: 4px;
}

/* Tab Container */
.tab-container {
  background: var(--color-white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

/* Tab Navigation */
.tab-nav {
  display: flex;
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
  padding: var(--space-2) var(--space-2) 0;
  gap: var(--space-1);
  overflow-x: auto;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: none;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
  position: relative;
  bottom: -1px;
}

.tab-button:hover {
  color: var(--color-charcoal);
  background: var(--color-gray-100);
}

.tab-button.active {
  color: var(--color-blue);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-bottom-color: var(--color-white);
}

.tab-icon {
  font-size: var(--font-size-base);
}

.tab-label {
  font-size: var(--font-size-sm);
}

/* Tab Content */
.tab-content {
  min-height: 400px;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .settings-view {
    padding: var(--space-4);
  }

  .page-title {
    font-size: var(--font-size-xl);
  }

  .tab-nav {
    padding: var(--space-2) var(--space-1) 0;
  }

  .tab-button {
    padding: var(--space-2) var(--space-3);
    flex: 1;
    justify-content: center;
  }

  .tab-label {
    display: none;
  }

  .tab-icon {
    font-size: var(--font-size-lg);
  }
}

@media (min-width: 769px) {
  .tab-icon {
    display: none;
  }
}
</style>
